import { Image } from '@tiptap/extension-image'
import { renderToString } from 'react-dom/server'

import { AsyncImageHtmlView } from './AsyncImageHtmlView.js'
import { AsyncImageNodeView } from './AsyncImageNodeView.js'
import {
  AsyncImageAttributes,
  ImageAlign,
  ImageDescriptionType,
  ImageSize,
} from './types.js'
import { staticMarkupToDOM } from './utils.js'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    'async-image': {
      /**
       * Add an image
       */
      setImage: (options: AsyncImageAttributes) => ReturnType
      setImageAttributes: (options: Partial<AsyncImageAttributes>) => ReturnType
    }
  }
}

const NAME = 'async-image'
export const AsyncImage = Image.extend({
  name: NAME,

  addAttributes() {
    return {
      ...this.parent?.(),
      'data-type': {
        default: 'image',
      },
      loading: {
        default: false,
        rendered: false,
      },
      hasDescription: {
        default: false,
        rendered: false,
        parseHTML: element => {
          if (element.tagName === 'img') {
            return element.getAttribute('alt').length > 0
          }
          const imageNodes = element.getElementsByTagName('img')
          if (imageNodes.length && imageNodes[0].getAttribute('alt')) {
            return true
          }
          const figcaptionNodes = element.getElementsByTagName('figcaption')
          if (figcaptionNodes.length) {
            return figcaptionNodes[0].innerText.length > 0
          }
          return false
        },
      },
      description: {
        default: null,
        rendered: false,
        parseHTML: element => {
          if (element.tagName === 'img') {
            return element.getAttribute('alt')
          }
          const imageNodes = element.getElementsByTagName('img')
          if (imageNodes.length && imageNodes[0].getAttribute('alt')) {
            return imageNodes[0].getAttribute('alt')
          }
          const figcaptionNodes = element.getElementsByTagName('figcaption')
          if (figcaptionNodes.length) {
            return figcaptionNodes[0].innerText
          }
          return null
        },
      },
      descriptionType: {
        default: ImageDescriptionType.Alt,
        rendered: false,
        parseHTML: element => {
          const imageNodes = element.getElementsByTagName('img')
          if (imageNodes.length && imageNodes[0].getAttribute('alt')) {
            return ImageDescriptionType.Alt
          }
          const figcaptionNodes = element.getElementsByTagName('figcaption')
          if (
            figcaptionNodes.length &&
            figcaptionNodes[0].getAttribute('data-as-overlay')
          ) {
            return ImageDescriptionType.Overlay
          }
          return ImageDescriptionType.Caption
        },
      },
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: (attributes: AsyncImageAttributes) => {
          return {
            'data-id': attributes.id,
          }
        },
      },
      size: {
        default: ImageSize.BestFit,
        parseHTML: element => element.getAttribute('data-size'),
        renderHTML: (attributes: AsyncImageAttributes) => {
          return {
            'data-size': attributes.size,
          }
        },
      },
      align: {
        default: ImageAlign.Center,
        parseHTML: element => element.getAttribute('data-align'),
        renderHTML: (attributes: AsyncImageAttributes) => {
          return {
            'data-align': attributes.align,
          }
        },
      },
      app: {
        default: undefined,
        parseHTML: element => element.getAttribute('data-app'),
        renderHTML: (attributes: AsyncImageAttributes) => {
          return {
            'data-app': attributes.app,
          }
        },
      },
      uploadId: {
        default: undefined,
        parseHTML: element => element.getAttribute('data-upload-id'),
        renderHTML: (attributes: AsyncImageAttributes) => {
          const { uploadId } = attributes
          return {
            'data-upload-id': uploadId,
          }
        },
      },
    }
  },
  addCommands() {
    return {
      setImage:
        options =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },
      setImageAttributes:
        attributes =>
        ({ commands }) =>
          commands.updateAttributes(NAME, attributes),
    }
  },

  parseHTML: () => [
    {
      tag: 'figure',
      getAttrs: element => {
        if (typeof element === 'string') {
          return false
        }

        const img = element.querySelector('img')
        const src = img?.getAttribute('src')
        if (!src) {
          return false
        }

        const alt = img?.getAttribute('alt')

        // Support for old image contents where id and app attributes are stored on the img element
        const id =
          element.getAttribute('data-id') || img?.getAttribute('data-id')
        const app =
          element.getAttribute('data-app') || img?.getAttribute('data-app')

        let description: string
        const figcaptionNodes = element.getElementsByTagName('figcaption')
        if (figcaptionNodes.length) {
          description = figcaptionNodes[0].innerText
        }

        return {
          src,
          id,
          app,
          alt,
          description,
        }
      },
    },
    {
      tag: 'img[src]',
    },
  ],

  renderHTML({ node, HTMLAttributes }) {
    /**
     * The result if this method is sent to API.
     * We only pass the data attributes as HTML renderer will be
     * in charge of translating these attributes to proper HTML markup
     * in read-only mode.
     */
    const { src, alt } = HTMLAttributes
    const attrs = node.attrs as AsyncImageAttributes
    return staticMarkupToDOM(
      renderToString(
        <AsyncImageHtmlView
          {...attrs}
          src={src as string}
          alt={alt as string}
        />,
      ),
    ).item(0)
  },

  addNodeView() {
    /**
     * Using ReactNodeViewRenderer will warp the node
     * in an extra HTML, which we don't have runtime access to.
     * Lack of runtime access means we can't add the proper alignment
     * classes to the container element and the images won't render
     * properly in the composer. That's why we have opted to use the
     * static renderer and document APIs..
     */
    return ({ node }) => {
      return {
        dom: staticMarkupToDOM(
          renderToString(
            AsyncImageNodeView(node.attrs as AsyncImageAttributes),
          ),
        ).item(0),
      }
    }
  },
})
